// Declarative step list for the guided product tour.
//
// The tour is a single continuous flow that auto-navigates between the
// Community feed, the four Analytics dashboards, and the Settings page. Each
// step either spotlights a specific element (matched by a ``data-tour``
// attribute) or shows a full-page/centered card (for the welcome, analytics,
// and finish steps).

export type StepPlacement = 'top' | 'bottom' | 'center';

export interface TourStep {
  /** Stable id (useful for analytics/debugging). */
  id: string;
  /** Route the app must be on for this step. */
  page: string;
  /**
   * CSS selector for the element to spotlight. When omitted (or when the
   * element is absent at runtime) the step renders as a centered card.
   */
  target?: string;
  title: string;
  body: string;
  /** Preferred tooltip placement relative to the target. */
  placement?: StepPlacement;
  /**
   * When false, no dark spotlight cutout is drawn and the scrim is kept light
   * so the page content behind stays visible (used for the analytics pages so
   * the charts are on display).
   */
  spotlight?: boolean;
  /**
   * Marks the analytics phase. Entering the first analytics step ensures demo
   * analytics data is prepared (for data-sparse tenants) before navigating.
   */
  analytics?: boolean;
  /**
   * For the hazard-card step: when the live feed is empty and no real card is
   * present, render an illustrative sample card so the step still works.
   */
  mockCardFallback?: boolean;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    page: '/app/community',
    title: 'Welcome to SafeWork',
    body: 'Your team is about to turn everyday hazard spotting into measurable safety wins. This 60-second tour shows how. Let’s go.',
    placement: 'center',
    spotlight: false,
  },
  {
    id: 'submit-hazard',
    page: '/app/community',
    target: '[data-tour="submit-hazard"]',
    title: 'Report a hazard in seconds',
    body: 'Tap the pink “+” to snap a photo and log a hazard from anywhere on site. AI auto-tags it, so frontline workers report in seconds, not paperwork.',
    placement: 'top',
  },
  {
    id: 'hazard-card',
    page: '/app/community',
    target: '[data-tour="hazard-card"]',
    title: 'Everything about a hazard, at a glance',
    body: 'Each card shows the photo, location, severity rating, tags, and comments. Rate the risk, discuss it with your crew, and mark it Resolved once it’s fixed.',
    placement: 'bottom',
    mockCardFallback: true,
  },
  {
    id: 'community-filter',
    page: '/app/community',
    target: '[data-tour="community-filter"]',
    title: 'Find exactly what matters',
    body: 'Filter the feed by severity, status, region, date, or keyword. Zero in on open extreme-risk hazards across any site in one tap.',
    placement: 'bottom',
  },
  {
    id: 'analytics-stats',
    page: '/app/communitydashboard',
    title: 'Turn reports into engagement',
    body: 'Gamified stats, streaks, and leaderboards keep your crews motivated to report, the leading indicator every safety program wants.',
    placement: 'bottom',
    spotlight: false,
    analytics: true,
  },
  {
    id: 'analytics-geo',
    page: '/app/communitydashboard/geo',
    title: 'See your risk on the map',
    body: 'Geographic hotspots reveal exactly where hazards cluster, by region and severity, so you deploy resources where they’ll prevent the most incidents.',
    placement: 'bottom',
    spotlight: false,
    analytics: true,
  },
  {
    id: 'analytics-ai',
    page: '/app/communitydashboard/ai',
    title: 'AI-powered risk intelligence',
    body: 'Sentiment and risk scoring surface your highest-consequence hazards and emerging trends automatically.',
    placement: 'bottom',
    spotlight: false,
    analytics: true,
  },
  {
    id: 'analytics-reports',
    page: '/app/communitydashboard/reports',
    title: 'Board-ready reporting',
    body: 'One-click summaries of reporting, resolution rates, and top risks give you the exact numbers you need for audits, clients, and leadership reviews.',
    placement: 'bottom',
    spotlight: false,
    analytics: true,
  },
  {
    id: 'settings-notifications',
    page: '/app/settings',
    target: '[data-tour="settings-notifications"]',
    title: 'Stay in the loop',
    body: 'Notifications keep you updated when hazards near you are reported, discussed, or resolved.',
    placement: 'bottom',
  },
  {
    id: 'settings-profile',
    page: '/app/settings',
    target: '[data-tour="settings-profile"]',
    title: 'Your profile',
    body: 'Manage your name, photo, and organization details here.',
    placement: 'bottom',
  },
  {
    id: 'settings-manager',
    page: '/app/settings',
    target: '[data-tour="settings-manager"]',
    title: 'Manager tools',
    body: 'Managers and admins manage user assignments from here.',
    placement: 'bottom',
  },
  {
    id: 'settings-admin',
    page: '/app/settings',
    target: '[data-tour="settings-admin"]',
    title: 'Admin controls',
    body: 'Admins manage the Admin Panel and organization-wide settings from here.',
    placement: 'bottom',
  },
  {
    id: 'settings-reportbug',
    page: '/app/settings',
    target: '[data-tour="settings-reportbug"]',
    title: 'We’re listening',
    body: 'Spot something off? Send us a bug report and our team will jump on it.',
    placement: 'top',
  },
  {
    id: 'settings-save',
    page: '/app/settings',
    target: '[data-tour="settings-save"]',
    title: 'Save your changes',
    body: 'Made adjustments? Save them here and you’re all set.',
    placement: 'top',
  },
  {
    id: 'settings-tutorial',
    page: '/app/settings',
    target: '[data-tour="settings-tutorial"]',
    title: 'Replay this tour anytime',
    body: 'Need a refresher, or onboarding a teammate? Launch this walkthrough again right here, whenever you like.',
    placement: 'top',
  },
  {
    id: 'finish',
    page: '/app/settings',
    title: 'You’re ready to make work safer',
    body: 'That’s the whole loop: spot a hazard, report it, resolve it, and watch your safety metrics climb. Your team’s next win starts now.',
    placement: 'center',
    spotlight: false,
  },
];

/** localStorage key marking that the user has completed/dismissed the tour. */
export const TUTORIAL_SEEN_KEY = 'safework_tutorial_completed';
